import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { defaultSettings } from '../pages/owner/settings/settingsData';
import { fetchPlatformSettings, savePlatformSettings } from '../services/owner/platformApi';
import {
  formatPlatformCompact,
  formatPlatformPrice,
  getPlatformSettings,
  setPlatformSettingsCache,
} from '../utils/platformMoney';

const PlatformSettingsContext = createContext(null);

export function PlatformSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => getPlatformSettings());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const applySettings = useCallback((next) => {
    const cached = setPlatformSettingsCache(next);
    setSettings(cached);
    return cached;
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchPlatformSettings();
      applySettings(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load settings');
      applySettings(defaultSettings);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const saveSettings = useCallback(async (next) => {
    await savePlatformSettings(next);
    return applySettings(next);
  }, [applySettings]);

  const formatPrice = useCallback(
    (amount) => formatPlatformPrice(amount, settings),
    [settings],
  );

  const formatCompact = useCallback(
    (amount) => formatPlatformCompact(amount, settings),
    [settings],
  );

  const value = useMemo(() => ({
    settings,
    loading,
    error,
    refresh,
    saveSettings,
    applySettings,
    formatPrice,
    formatCompact,
    softwareName: settings.softwareName,
    currency: settings.currency,
  }), [settings, loading, error, refresh, saveSettings, applySettings, formatPrice, formatCompact]);

  return (
    <PlatformSettingsContext.Provider value={value}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings() {
  const context = useContext(PlatformSettingsContext);
  if (!context) {
    throw new Error('usePlatformSettings must be used within PlatformSettingsProvider');
  }
  return context;
}
